import { queryOptions, useMutation, useQueryClient } from "@tanstack/react-query";
import {
  addGroupApp,
  bulkAddGroupApps,
  auditGroup,
  auditAllGroups,
  createGroup,
  createGroupVariable,
  createGroupDimension,
  createGroupMeasure,
  deleteGroup,
  deleteGroupVariable,
  deleteGroupDimension,
  deleteGroupMeasure,
  getGroup,
  listGroups,
  removeGroupApp,
  updateGroup,
  updateGroupVariable,
  updateGroupDimension,
  updateGroupMeasure,
  syncGroup,
  syncAllGroups,
  type SyncGroupOptions,
} from "@/lib/api/groups";
import type {
  AddGroupAppInput,
  BulkAddGroupAppsInput,
  CreateGroupInput,
  CreateGroupVariableInput,
  CreateGroupDimensionInput,
  CreateGroupMeasureInput,
  Group,
  GroupApp,
  GroupDetail,
  GroupVariable,
  GroupDimension,
  GroupMeasure,
  GroupWithCounts,
  UpdateGroupInput,
  UpdateGroupVariableInput,
  UpdateGroupDimensionInput,
  UpdateGroupMeasureInput,
} from "@repo/shared";

// =============================================================================
// Query Keys Factory
// =============================================================================

export const groupKeys = {
  all: ["groups"] as const,
  lists: () => [...groupKeys.all, "list"] as const,
  list: (filters?: { limit?: number; cursor?: string }) => [...groupKeys.lists(), filters] as const,
  details: () => [...groupKeys.all, "detail"] as const,
  detail: (id: string) => [...groupKeys.details(), id] as const,
  apps: (groupId: string) => [...groupKeys.detail(groupId), "apps"] as const,
  variables: (groupId: string) => [...groupKeys.detail(groupId), "variables"] as const,
  variable: (groupId: string, variableId: string) =>
    [...groupKeys.variables(groupId), variableId] as const,
  dimensions: (groupId: string) => [...groupKeys.detail(groupId), "dimensions"] as const,
  dimension: (groupId: string, dimensionId: string) =>
    [...groupKeys.dimensions(groupId), dimensionId] as const,
  measures: (groupId: string) => [...groupKeys.detail(groupId), "measures"] as const,
  measure: (groupId: string, measureId: string) =>
    [...groupKeys.measures(groupId), measureId] as const,
};

// =============================================================================
// Query Options
// =============================================================================

export const groupsQueryOptions = (limit?: number, cursor?: string) =>
  queryOptions({
    queryKey: groupKeys.list({ limit, cursor }),
    queryFn: () => listGroups({ limit, cursor }),
  });

export function groupDetailQueryOptions(id: string) {
  return queryOptions({
    queryKey: groupKeys.detail(id),
    queryFn: () => getGroup(id),
    enabled: !!id,
  });
}

// =============================================================================
// Group Mutations
// =============================================================================

export function useCreateGroup() {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: (data: CreateGroupInput) => createGroup(data),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: groupKeys.lists() });
    },
  });
}

export function useUpdateGroup() {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: ({ id, data }: { id: string; data: UpdateGroupInput }) => updateGroup(id, data),
    onSuccess: (_, { id }) => {
      queryClient.invalidateQueries({ queryKey: groupKeys.lists() });
      queryClient.invalidateQueries({ queryKey: groupKeys.detail(id) });
    },
  });
}

export function useDeleteGroup() {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: (id: string) => deleteGroup(id),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: groupKeys.lists() });
    },
  });
}

// =============================================================================
// Group Apps Mutations
// =============================================================================

export function useAddGroupApp() {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: ({ groupId, data }: { groupId: string; data: AddGroupAppInput }) =>
      addGroupApp(groupId, data),
    onSuccess: (_, { groupId }) => {
      queryClient.invalidateQueries({ queryKey: groupKeys.detail(groupId) });
      queryClient.invalidateQueries({ queryKey: groupKeys.lists() });
    },
  });
}

export function useBulkAddGroupApps() {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: ({ groupId, data }: { groupId: string; data: BulkAddGroupAppsInput }) =>
      bulkAddGroupApps(groupId, data),
    onSuccess: (_, { groupId }) => {
      queryClient.invalidateQueries({ queryKey: groupKeys.detail(groupId) });
      queryClient.invalidateQueries({ queryKey: groupKeys.lists() });
    },
  });
}

export function useRemoveGroupApp() {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: ({ groupId, groupAppId }: { groupId: string; groupAppId: string }) =>
      removeGroupApp(groupId, groupAppId),
    onSuccess: (_, { groupId }) => {
      queryClient.invalidateQueries({ queryKey: groupKeys.detail(groupId) });
      queryClient.invalidateQueries({ queryKey: groupKeys.lists() });
    },
  });
}

// =============================================================================
// Group Variables Mutations
// =============================================================================

export function useCreateGroupVariable() {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: ({ groupId, data }: { groupId: string; data: CreateGroupVariableInput }) =>
      createGroupVariable(groupId, data),
    onSuccess: (_, { groupId }) => {
      queryClient.invalidateQueries({ queryKey: groupKeys.detail(groupId) });
      queryClient.invalidateQueries({ queryKey: groupKeys.lists() });
    },
  });
}

export function useUpdateGroupVariable() {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: ({
      groupId,
      variableId,
      data,
    }: {
      groupId: string;
      variableId: string;
      data: UpdateGroupVariableInput;
    }) => updateGroupVariable(groupId, variableId, data),
    onSuccess: (_, { groupId }) => {
      queryClient.invalidateQueries({ queryKey: groupKeys.detail(groupId) });
    },
  });
}

export function useDeleteGroupVariable() {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: ({ groupId, variableId }: { groupId: string; variableId: string }) =>
      deleteGroupVariable(groupId, variableId),
    onSuccess: (_, { groupId }) => {
      queryClient.invalidateQueries({ queryKey: groupKeys.detail(groupId) });
      queryClient.invalidateQueries({ queryKey: groupKeys.lists() });
    },
  });
}

// =============================================================================
// Group Dimensions Mutations
// =============================================================================

export function useCreateGroupDimension() {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: ({ groupId, data }: { groupId: string; data: CreateGroupDimensionInput }) =>
      createGroupDimension(groupId, data),
    onSuccess: (_, { groupId }) => {
      queryClient.invalidateQueries({ queryKey: groupKeys.detail(groupId) });
      queryClient.invalidateQueries({ queryKey: groupKeys.lists() });
    },
  });
}

export function useUpdateGroupDimension() {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: ({
      groupId,
      dimensionId,
      data,
    }: {
      groupId: string;
      dimensionId: string;
      data: UpdateGroupDimensionInput;
    }) => updateGroupDimension(groupId, dimensionId, data),
    onSuccess: (_, { groupId }) => {
      queryClient.invalidateQueries({ queryKey: groupKeys.detail(groupId) });
    },
  });
}

export function useDeleteGroupDimension() {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: ({ groupId, dimensionId }: { groupId: string; dimensionId: string }) =>
      deleteGroupDimension(groupId, dimensionId),
    onSuccess: (_, { groupId }) => {
      queryClient.invalidateQueries({ queryKey: groupKeys.detail(groupId) });
      queryClient.invalidateQueries({ queryKey: groupKeys.lists() });
    },
  });
}

// =============================================================================
// Group Measures Mutations
// =============================================================================

export function useCreateGroupMeasure() {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: ({ groupId, data }: { groupId: string; data: CreateGroupMeasureInput }) =>
      createGroupMeasure(groupId, data),
    onSuccess: (_, { groupId }) => {
      queryClient.invalidateQueries({ queryKey: groupKeys.detail(groupId) });
      queryClient.invalidateQueries({ queryKey: groupKeys.lists() });
    },
  });
}

export function useUpdateGroupMeasure() {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: ({
      groupId,
      measureId,
      data,
    }: {
      groupId: string;
      measureId: string;
      data: UpdateGroupMeasureInput;
    }) => updateGroupMeasure(groupId, measureId, data),
    onSuccess: (_, { groupId }) => {
      queryClient.invalidateQueries({ queryKey: groupKeys.detail(groupId) });
    },
  });
}

export function useDeleteGroupMeasure() {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: ({ groupId, measureId }: { groupId: string; measureId: string }) =>
      deleteGroupMeasure(groupId, measureId),
    onSuccess: (_, { groupId }) => {
      queryClient.invalidateQueries({ queryKey: groupKeys.detail(groupId) });
      queryClient.invalidateQueries({ queryKey: groupKeys.lists() });
    },
  });
}

// =============================================================================
// Sync Operations
// =============================================================================

export function useAuditGroup() {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: (groupId: string) => auditGroup(groupId),
    onSuccess: (_, groupId) => {
      // Invalidate group detail to refresh sync status after audit completes
      queryClient.invalidateQueries({ queryKey: groupKeys.detail(groupId) });
    },
  });
}

export function useSyncGroup() {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: ({ groupId, options }: { groupId: string; options?: SyncGroupOptions }) =>
      syncGroup(groupId, options),
    onSuccess: (_, { groupId }) => {
      // Invalidate group detail to refresh sync status after sync completes
      queryClient.invalidateQueries({ queryKey: groupKeys.detail(groupId) });
    },
  });
}

export function useAuditAllGroups() {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: () => auditAllGroups(),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: groupKeys.lists() });
    },
  });
}

export function useSyncAllGroups() {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: () => syncAllGroups(),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: groupKeys.lists() });
    },
  });
}

// Re-export types
export type {
  Group,
  GroupApp,
  GroupDetail,
  GroupVariable,
  GroupDimension,
  GroupMeasure,
  GroupWithCounts,
};
